import { useState, useMemo } from "react";
import { Play, Filter } from "lucide-react";
import { ChapterHeader, FadeUp } from "./Motion";
import { trackEvent } from "@/lib/api";

export default function VideoLibrary({ content, onPlay }) {
    const [type, setType] = useState("all");
    const [weight, setWeight] = useState("all");
    const [q, setQ] = useState("");

    const videos = content.videoLibrary || [];
    const weights = useMemo(() => Array.from(new Set(videos.map((v) => v.weightClass).filter(Boolean))), [videos]);

    const filtered = videos.filter(
        (v) =>
            (type === "all" || v.type === type) &&
            (weight === "all" || v.weightClass === weight) &&
            (q === "" || (v.title + v.event + v.description).toLowerCase().includes(q.toLowerCase()))
    );

    return (
        <section id="highlights" className="relative py-20 md:py-32 border-t border-white/10">
            <div className="max-w-[1600px] mx-auto px-5 md:px-10">
                <ChapterHeader num="02" kicker="Film Room" title="Featured Wrestling Highlights" testId="chapter-highlights" />

                {/* Featured card */}
                <FadeUp>
                    <button
                        onClick={() => {
                            trackEvent("video_play");
                            onPlay(content.featuredVideo);
                        }}
                        className="group grid grid-cols-1 md:grid-cols-12 gap-0 border border-white/10 hover:border-brand-gold/60 bg-black text-left w-full"
                        data-testid="featured-video-card"
                    >
                        <div className="md:col-span-8 relative aspect-video bg-black overflow-hidden">
                            <video
                                src={content.featuredVideo.url}
                                muted
                                playsInline
                                preload="metadata"
                                className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity"
                            />
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-16 h-16 md:w-20 md:h-20 bg-brand-gold flex items-center justify-center transition-transform group-hover:scale-110">
                                    <Play size={28} fill="black" className="ml-1" />
                                </div>
                            </div>
                            <div className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.25em] uppercase bg-black/70 px-3 py-1 border border-white/10">
                                Signature Reel
                            </div>
                        </div>
                        <div className="md:col-span-4 p-6 md:p-8 flex flex-col justify-between">
                            <div>
                                <div className="chapter-num">Now Featured</div>
                                <h3 className="font-mono uppercase text-2xl md:text-3xl mt-3 leading-tight">
                                    {content.featuredVideo.title}
                                </h3>
                                <p className="editorial text-white/70 text-lg mt-4">
                                    {content.featuredVideo.description}
                                </p>
                            </div>
                            <div className="mt-6 flex items-center justify-between text-[10px] font-mono tracking-[0.2em] uppercase text-white/50">
                                <span>138 LB</span>
                                <span>NHSCA NATIONALS</span>
                                <span className="text-brand-gold">▶ PLAY</span>
                            </div>
                        </div>
                    </button>
                </FadeUp>

                {/* Filters */}
                <FadeUp delay={0.1}>
                    <div className="mt-16 border-t border-white/10 pt-8">
                        <div className="flex items-baseline justify-between flex-wrap gap-4 mb-6">
                            <div>
                                <div className="chapter-num">02 / B</div>
                                <h3 className="font-mono uppercase text-2xl md:text-3xl mt-2">Match Video Library</h3>
                            </div>
                            <div className="flex items-center gap-2 text-white/60 font-mono text-xs uppercase tracking-[0.2em]">
                                <Filter size={14} /> Filter
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-8">
                            <input
                                placeholder="SEARCH TITLE, EVENT, KEYWORD"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                className="bg-black px-4 py-3 font-mono text-xs uppercase tracking-[0.15em] placeholder:text-white/30 outline-none focus:text-brand-gold"
                                data-testid="video-search"
                            />
                            <select
                                value={type}
                                onChange={(e) => setType(e.target.value)}
                                className="bg-black px-4 py-3 font-mono text-xs uppercase tracking-[0.15em] outline-none"
                                data-testid="video-type-filter"
                            >
                                <option value="all">ALL TYPES</option>
                                <option value="highlight">HIGHLIGHTS</option>
                                <option value="full_match">FULL MATCHES</option>
                            </select>
                            <select
                                value={weight}
                                onChange={(e) => setWeight(e.target.value)}
                                className="bg-black px-4 py-3 font-mono text-xs uppercase tracking-[0.15em] outline-none"
                                data-testid="video-weight-filter"
                            >
                                <option value="all">ALL WEIGHTS</option>
                                {weights.map((w) => (
                                    <option key={w} value={w}>
                                        {w} LB
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="border border-white/10 divide-y divide-white/10">
                            {filtered.length === 0 && (
                                <div className="p-8 font-mono text-sm text-white/50 uppercase tracking-[0.15em]">
                                    No videos match the current filter.
                                </div>
                            )}
                            {filtered.map((v, i) => (
                                <button
                                    key={v.id}
                                    onClick={() => {
                                        trackEvent("video_play");
                                        onPlay(v);
                                    }}
                                    className="w-full grid grid-cols-12 gap-4 items-center p-4 md:p-6 hover:bg-white/5 transition-colors text-left group"
                                    data-testid={`video-row-${v.id}`}
                                >
                                    <span className="col-span-1 chapter-num">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <div className="col-span-7 md:col-span-6">
                                        <div className="font-mono uppercase text-base md:text-lg group-hover:text-brand-gold transition-colors">
                                            {v.title}
                                        </div>
                                        <div className="chapter-num mt-1">
                                            {v.event} <span className="text-white/30">·</span>{" "}
                                            {v.type === "highlight" ? "Highlight" : "Full Match"}
                                        </div>
                                    </div>
                                    <div className="hidden md:block col-span-2 font-mono text-xs uppercase tracking-[0.2em] text-white/60">
                                        {v.weightClass ? `${v.weightClass} LB` : "—"}
                                    </div>
                                    <div className="col-span-3 md:col-span-2 font-mono text-xs uppercase tracking-[0.2em] text-brand-gold text-right">
                                        {v.result}
                                    </div>
                                    <div className="col-span-1 flex justify-end">
                                        <Play size={16} className="text-white/50 group-hover:text-brand-gold" />
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </FadeUp>
            </div>
        </section>
    );
}
